import DocsFileIcon from '@assets/icons/docs-file.svg?react';
import PDFFileIcon from '@assets/icons/pdf-file.svg?react';
import { DropboxFileItem } from '@common/types/dropbox';
import { VictronImage } from '@components/display/images';
import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { APP_PREFIX_PATH } from '@constants/common';
import { useAppSelector } from '@hooks/generic';
import { isInfiniteLoadingItem, LoadableInfiniteItem, useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
    Box,
    IconButton,
    Skeleton,
    Stack,
    TableCell,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getDateFnsLocale, handleError } from '@utils/common';
import { formatTimeAgo } from '@utils/date';
import { formatBytes, triggerBrowserDownload } from '@utils/file';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Table } from './Table';

const IntlHeaders = [
    { id: 'name', label: defineMessage({ id: 'common.name', defaultMessage: 'Name' }), width: '50%' },
    { id: 'file_type', label: defineMessage({ id: 'common.type', defaultMessage: 'Type' }) },
    { id: 'size', label: defineMessage({ id: 'common.size', defaultMessage: 'Size' }) },
    { id: 'last_edited_at', label: defineMessage({ id: 'common.modified', defaultMessage: 'Modified' }) },
    { id: 'actions', label: defineMessage({ id: 'common.actions', defaultMessage: 'Actions' }), sortable: false },
];

type LoadableDropboxFileItem = LoadableInfiniteItem<DropboxFileItem>;

type ToggleFavoriteArgs = {
    path: string;
    folderPath: string;
};

type LoadDropboxPageArgs = {
    queryPath: string;
    sortParam: string | null;
    pageSize: number;
    loadedPaths: string[];
};

type DropboxFilesListTableProps = {
    routeSegment: string;
    downloadType: string;
    emptyListText: MessageDescriptor;
    loadErrorMessage: MessageDescriptor;
    loadPage: (args: LoadDropboxPageArgs) => Promise<{ results: DropboxFileItem[]; count?: number }>;
    onFavorite?: (args: ToggleFavoriteArgs) => Promise<unknown>;
    onUnfavorite?: (args: ToggleFavoriteArgs) => Promise<unknown>;
    externalFavoriteStates?: Record<string, boolean>;
    onFavoriteStateChange?: (args: { path: string; isFavorite: boolean }) => void;
    onPreviewRowClick?: (item: DropboxFileItem) => void;
    onVisibleMediaItemsChange?: (items: DropboxFileItem[]) => void;
};

export const DropboxFilesListTable = ({
    routeSegment,
    downloadType,
    emptyListText,
    loadErrorMessage,
    loadPage,
    onFavorite,
    onUnfavorite,
    externalFavoriteStates,
    onFavoriteStateChange,
    onPreviewRowClick,
    onVisibleMediaItemsChange,
}: DropboxFilesListTableProps) => {
    const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
    const [favoritingFiles, setFavoritingFiles] = useState<Set<string>>(new Set());
    const [favoriteOverrides, setFavoriteOverrides] = useState<Record<string, boolean>>({});

    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const pageSize = isLg ? 15 : isMd ? 15 : isSm ? 15 : 10;

    const [params, setParams] = useSearchParams();
    const sortParam = params.get('sort');
    const sort: { column: string; direction: 'asc' | 'desc' } = {
        column: sortParam?.replace(/^-/, '') ?? '',
        direction: sortParam?.startsWith('-') ? 'desc' : 'asc',
    };

    const { pathname } = useLocation();
    const queryPath = pathname.split('/').filter(Boolean).slice(2).map(decodeURIComponent).join('/');

    const scrollElement = useMaxWidthWrapperScrollElement();
    const intl = useIntl();
    const currentLocale = useAppSelector((state) => state.translation.locale);
    const dateLocale = getDateFnsLocale(currentLocale);

    const headers = useMemo(
        () => IntlHeaders.map((header) => ({ ...header, label: intl.formatMessage(header.label).toUpperCase() })),
        [intl]
    );

    const getDisplayIsFavorite = useCallback(
        (item: DropboxFileItem) => {
            const itemPath = item.path ?? '';
            if (itemPath in (externalFavoriteStates ?? {})) {
                return Boolean(externalFavoriteStates?.[itemPath]);
            }

            return itemPath in favoriteOverrides ? favoriteOverrides[itemPath] : Boolean(item.is_favorite);
        },
        [externalFavoriteStates, favoriteOverrides]
    );

    const loadDropboxPage = useCallback(
        async ({ pageSize: nextPageSize, loadedPaths }: { page: number; pageSize: number; loadedPaths?: string[] }) =>
            loadPage({
                queryPath,
                sortParam,
                pageSize: nextPageSize,
                loadedPaths: loadedPaths ?? [],
            }),
        [loadPage, queryPath, sortParam]
    );

    const { items, sentinelRef, hasRealItems, isLoadingMore } = useInfiniteLazyLoader<DropboxFileItem>({
        pageSize,
        scrollElement,
        contextKey: `${queryPath}::${sortParam ?? ''}::${pageSize}`,
        loadPage: loadDropboxPage,
        getItemKey: (item) => item.path ?? item.name ?? '',
        getLoadedPaths: (loadedItems) => loadedItems.map((item) => item.path ?? '').filter(Boolean),
        countStrategy: 'remaining',
        onError: (requestError) => {
            console.error(intl.formatMessage(loadErrorMessage), requestError);
            handleError(intl.formatMessage(loadErrorMessage), requestError);
        },
    });

    const isEmpty = useMemo(() => !isLoadingMore && !hasRealItems, [hasRealItems, isLoadingMore]);

    const handleSortClick = (column: string | number) => {
        setParams((prev) => {
            const newParams = new URLSearchParams(prev);
            const currentSort = newParams.get('sort');

            if (currentSort === column) {
                newParams.set('sort', `-${column}`);
            } else if (currentSort === `-${column}`) {
                newParams.delete('sort');
            } else {
                newParams.set('sort', column.toString());
            }
            return newParams;
        });
    };

    const getItemHref = (item: LoadableDropboxFileItem) => {
        if (isInfiniteLoadingItem(item) || item.file_type !== 'folder') {
            return '';
        }

        if (!item.path) {
            return '#';
        }

        const preservedParams = new URLSearchParams();
        const view = params.get('view');
        const queryPageSize = params.get('page_size');
        const sortValue = params.get('sort');

        if (view) preservedParams.set('view', view);
        if (queryPageSize) preservedParams.set('page_size', queryPageSize);
        if (sortValue) preservedParams.set('sort', sortValue);

        const queryString = preservedParams.toString();

        return `${APP_PREFIX_PATH}/${routeSegment}/${item.path.replace(/^\/+/, '')}${queryString ? `?${queryString}` : ''}`;
    };

    const handleDownload = async (item: DropboxFileItem) => {
        const itemPath = item.path ?? '';
        if (!itemPath || downloadingFiles.has(itemPath)) {
            return;
        }

        setDownloadingFiles((prev) => new Set(prev).add(itemPath));
        try {
            await triggerBrowserDownload(
                `/api-professional/dropbox/download/?path=${encodeURIComponent(itemPath)}&type=${downloadType}`
            );
        } finally {
            setDownloadingFiles((prev) => {
                const next = new Set(prev);
                next.delete(itemPath);
                return next;
            });
        }
    };

    const handleToggleFavorite = useCallback(
        async (item: DropboxFileItem, isFavorite: boolean) => {
            const itemPath = item.path ?? '';
            const nextFavorite = !isFavorite;
            if (!itemPath || favoritingFiles.has(itemPath) || !onFavorite || !onUnfavorite) {
                return;
            }

            setFavoritingFiles((prev) => new Set(prev).add(itemPath));
            setFavoriteOverrides((prev) => ({ ...prev, [itemPath]: nextFavorite }));
            onFavoriteStateChange?.({ path: itemPath, isFavorite: nextFavorite });

            try {
                if (isFavorite) {
                    await onUnfavorite({
                        path: itemPath,
                        folderPath: queryPath,
                    });
                } else {
                    await onFavorite({
                        path: itemPath,
                        folderPath: queryPath,
                    });
                }
            } catch (error) {
                setFavoriteOverrides((prev) => ({ ...prev, [itemPath]: isFavorite }));
                onFavoriteStateChange?.({ path: itemPath, isFavorite });
                handleError(
                    intl.formatMessage({
                        id: 'filelist.favoriteError',
                        defaultMessage: 'Error updating favorite status',
                    }),
                    error
                );
            } finally {
                setFavoritingFiles((prev) => {
                    const next = new Set(prev);
                    next.delete(itemPath);
                    return next;
                });
            }
        },
        [favoritingFiles, intl, onFavorite, onFavoriteStateChange, onUnfavorite, queryPath]
    );

    const renderTableRow = (item: LoadableDropboxFileItem) => {
        if (isInfiniteLoadingItem(item)) {
            return (
                <>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <TableCell key={i}>
                            <Skeleton variant="text" />
                        </TableCell>
                    ))}
                </>
            );
        }

        const isFavorite = getDisplayIsFavorite(item);

        return (
            <>
                <TableCell className="flex flex-row gap-2 items-center">
                    {['folder', 'file'].includes(item.file_type ?? '') ? (
                        <Box
                            height={40}
                            width={50}
                            bgcolor={(muiTheme) => muiTheme.palette['neutral-100'].main}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="1px"
                            flexShrink={0}
                        >
                            {item.file_type === 'folder' ? (
                                <FolderOutlinedIcon sx={{ fontSize: 24 }} color="primary" />
                            ) : item.name?.endsWith('.pdf') ? (
                                <PDFFileIcon width={24} height={24} />
                            ) : item.name?.endsWith('.doc') || item.name?.endsWith('.docx') ? (
                                <DocsFileIcon width={24} height={24} />
                            ) : (
                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 24 }} color="primary" />
                            )}
                        </Box>
                    ) : (
                        <VictronImage
                            src={item.thumbnail}
                            decoding="async"
                            alt={intl.formatMessage(
                                { id: 'media.thumbnail', defaultMessage: 'Media Item Thumbnail of {name}' },
                                { name: item.name }
                            )}
                            className="w-[50px] h-[40px] object-cover"
                        />
                    )}
                    <Typography fontFamily="museo-sans" fontSize={14} letterSpacing={0} className="line-clamp-2">
                        {item.name}
                    </Typography>
                    {isFavorite && onFavorite && onUnfavorite && (
                        <StarIcon sx={{ fontSize: 16 }} color="primary-300" />
                    )}
                </TableCell>
                <TableCell>
                    <Box border={2} borderRadius="7px" p="3px" width="fit-content">
                        <Typography fontSize="12px" fontWeight={500} lineHeight="14px">
                            {item.file_type === 'folder'
                                ? 'FOLDER'
                                : `.${item.path?.split('.').pop()?.toUpperCase() ?? item.file_type?.toUpperCase()}`}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell>{item.size ? formatBytes(item.size) : '-'}</TableCell>
                <TableCell>
                    <Tooltip
                        title={
                            item.last_edited_at
                                ? format(new Date(item.last_edited_at), 'dd/MM/yyyy', { locale: dateLocale })
                                : '-'
                        }
                        placement="top"
                    >
                        <span>{formatTimeAgo(item.last_edited_at)}</span>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Stack direction="row" gap={1} alignItems="center">
                        <IconButton
                            size="small"
                            color="white"
                            className="z-50"
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDownload(item);
                            }}
                            loading={downloadingFiles.has(item.path ?? '')}
                        >
                            <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        {onFavorite && onUnfavorite && (
                            <IconButton
                                size="small"
                                color="neutral-500"
                                className="z-50"
                                sx={{ '&:hover': { bgcolor: 'neutral-300.main' } }}
                                loading={favoritingFiles.has(item.path ?? '')}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleToggleFavorite(item, isFavorite);
                                }}
                            >
                                {isFavorite ? (
                                    <StarIcon sx={{ fontSize: 16 }} color="primary-300" />
                                ) : (
                                    <StarBorderIcon sx={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                        )}
                    </Stack>
                </TableCell>
            </>
        );
    };

    useEffect(() => {
        setFavoriteOverrides({});
    }, [queryPath, sortParam, pageSize]);

    useEffect(() => {
        if (!onVisibleMediaItemsChange) {
            return;
        }

        const visibleItems = items.filter(
            (item): item is DropboxFileItem =>
                !isInfiniteLoadingItem(item) && ['image', 'video'].includes(item.file_type ?? '')
        );
        onVisibleMediaItemsChange(visibleItems);
    }, [items, onVisibleMediaItemsChange]);

    return (
        <div className={clsx('flex flex-col', { 'h-full': isEmpty })}>
            <Table
                rootClassname="bg-white"
                headers={headers}
                data={items}
                selectionKey="name"
                renderRow={renderTableRow}
                emptyListTxt={intl.formatMessage(emptyListText)}
                onSortClick={handleSortClick}
                sort={sort}
                separator="gray"
                getHref={getItemHref}
                fillMissingRows={false}
                pagination={false}
                centerEmptyListTxt={isEmpty}
                emptyMinHeight={false}
                tableRowProps={(item) =>
                    isInfiniteLoadingItem(item) || item.file_type === 'file'
                        ? { className: 'hover:cursor-default' }
                        : {}
                }
                onRowClick={(item) => {
                    if (isInfiniteLoadingItem(item) || ['folder', 'file'].includes(item.file_type ?? '')) {
                        return;
                    }

                    const isFavorite = getDisplayIsFavorite(item);
                    onPreviewRowClick?.({
                        ...item,
                        is_favorite: isFavorite,
                    });
                }}
            />
            <div ref={sentinelRef} className="h-px w-full" aria-hidden />
        </div>
    );
};
